import { SelectC } from 'components'
import { useEffect, useState } from 'react'
import { useLoadOrderSellers } from 'modules/orders/hooks'
import { useDebounce } from 'utils'
import { useOrderStore } from 'modules/orders/store'
import { useFormContext } from 'react-hook-form'

/** Persist API division as string on the form for commission checks. */
function sellerWithDivisionString(seller: any) {
  if (!seller?.id) return seller
  const d = seller.division
  return {
    ...seller,
    division: d == null || d === '' ? undefined : String(d).trim() || undefined
  }
}

export const SelectSeller = () => {
  const [searchCustomer, setSearchCustomer] = useState<string>('')
  const searchCustomerDebounced = useDebounce(searchCustomer, 250)
  const setForm = useOrderStore.useSetForm()
  const form = useOrderStore.useForm()
  const { setValue, watch } = useFormContext()
  const sellerFormValue = watch('seller')

  const { data: sellers, isLoading: sellersLoading } = useLoadOrderSellers({
    page: 1,
    limit: 100,
    search: searchCustomerDebounced,
    filters: {}
  })

  const handleChangeCustomer = (value: string, reason: string) => {
    if (reason === 'input' || reason === 'clear') setSearchCustomer(value)
  }

  useEffect(() => {
    if (sellersLoading || !sellers) return

    const storeSeller = form?.seller
    const formSeller = sellerFormValue
    const defaultSeller = sellers?.defaultValue
    const selectedSellerId = formSeller?.id || storeSeller?.id
    const selectedSellerFromApi =
      sellers?.data?.find((seller: any) => seller?.id === selectedSellerId) ??
      (defaultSeller?.id === selectedSellerId ? defaultSeller : undefined)

    // When navigating back to the page, the store may still have the seller,
    // but the form context gets re-created and loses that value.
    if (storeSeller?.id && !formSeller?.id) {
      const raw = selectedSellerFromApi || storeSeller
      const hydrated = sellerWithDivisionString(raw)
      setValue('seller', hydrated, { shouldValidate: false })
      setForm({ seller: hydrated })
      return
    }

    // Keep seller payload hydrated with latest API fields (e.g. division).
    if (formSeller?.id && selectedSellerFromApi) {
      const next = sellerWithDivisionString(selectedSellerFromApi)
      const hasDivisionChanged =
        String(formSeller?.division ?? '') !== String(next?.division ?? '')

      if (hasDivisionChanged) {
        setForm({ seller: next })
        setValue('seller', next, { shouldValidate: false })
      }
    }

    // Otherwise make sure we always have the default seller selected.
    if (!storeSeller?.id && !formSeller?.id && defaultSeller?.id) {
      const next = sellerWithDivisionString(defaultSeller)
      setForm({ seller: next })
      setValue('seller', next, { shouldValidate: false })
    }
  }, [
    form?.seller,
    sellerFormValue,
    sellers,
    sellers?.defaultValue,
    setForm,
    setValue,
    sellersLoading
  ])

  return (
    <SelectC
      name="seller"
      label="Vendedor"
      options={sellers?.data}
      onInputChange={(e, value, reason) => handleChangeCustomer(value, reason)}
      onChange={(_, item) => {
        if (!item?.id) {
          setForm({ seller: null })
          setValue('seller', null, { shouldValidate: true, shouldDirty: true })
          return
        }
        const next = sellerWithDivisionString(item)
        setForm({ seller: next })
        setValue('seller', next, { shouldValidate: true, shouldDirty: true })
      }}
      getOptionLabel={(option: any) => option?.id + ' - ' + option?.name}
      loading={sellersLoading}
    />
  )
}
